import { err, notFound, ok, validate, type Result } from '../errors/errors';
import type { Deps } from '../interfaces/stores';
import type { Format } from '../schemas/format';
import { ALL_SHEET_ID, sheetsFor, type SheetLayout } from '../domain/sheets';
import { FlowCellSchema, FlowRowSchema, type FlowCell, type FlowRow, type Mark } from '../schemas/entities';

type D<K extends keyof Deps> = Pick<Deps, K>;

export const cellId = (rowId: string, segmentId: string) => `${rowId}~${segmentId}`;

/**
 * Cross-ex and crossfire notes are one big box per period, not one per row (questions jump between arguments too
 * fast to follow row by row). They're stored as a cell on this pseudo-row, which never appears in the rows table.
 */
export const CX_ROW_ID = 'cx';

/** Rows are ordered by a numeric rank; inserting between two rows takes the midpoint. */
export const rankBetween = (before: number | undefined, after: number | undefined): number => {
  if (before === undefined && after === undefined) return 1000;
  if (before === undefined) return after! - 1000;
  if (after === undefined) return before + 1000;
  return (before + after) / 2;
};

export const addRow = (d: D<'flow' | 'clock' | 'ids'>) =>
  /** `id` lets the UI show the row (and put the cursor in it) before the write finishes. */
  async (roundId: string, sheetId: string, position: { after?: string; before?: string; first?: boolean; id?: string } = {}): Promise<Result<FlowRow>> => {
    const rows = (await d.flow.rows(roundId)).filter((r) => r.sheetId === sheetId).sort((a, b) => a.rank - b.rank);
    let rank: number;
    if (position.after) {
      const i = rows.findIndex((r) => r.id === position.after);
      if (i < 0) return err(notFound('row', position.after));
      rank = rankBetween(rows[i]!.rank, rows[i + 1]?.rank);
    } else if (position.before) {
      const i = rows.findIndex((r) => r.id === position.before);
      if (i < 0) return err(notFound('row', position.before));
      rank = rankBetween(rows[i - 1]?.rank, rows[i]!.rank);
    } else if (position.first) {
      // Below any rank rankBetween can produce for an empty sheet, so it stays first even if starter rows land meanwhile.
      rank = rows.length ? rows[0]!.rank - 1000 : 0;
    } else {
      rank = rankBetween(rows.at(-1)?.rank, undefined);
    }
    const now = d.clock.now();
    const row: FlowRow = { id: position.id ?? d.ids.next(), roundId, sheetId, rank, createdAt: now, updatedAt: now };
    await d.flow.putRow(row);
    return ok(row);
  };

/** Make sure every sheet has at least a few empty rows to type into. */
export const ensureRows = (d: D<'flow' | 'clock' | 'ids' | 'tx'>) => (roundId: string, sheetIds: string[], min = 6): Promise<void> =>
  // In a transaction so two concurrent calls can't both see "0 rows" and double-create.
  d.tx.run(async () => {
    const rows = await d.flow.rows(roundId);
    for (const sheetId of sheetIds) {
      const have = rows.filter((r) => r.sheetId === sheetId).length;
      for (let i = have; i < min; i++) await addRow(d)(roundId, sheetId);
    }
  });

// Text and marks share one record; transactions keep a mark toggle and a text save from clobbering each other.
export const writeCell = (d: D<'flow' | 'clock' | 'tx'>) =>
  (roundId: string, rowId: string, segmentId: string, text: string): Promise<Result<FlowCell>> =>
    d.tx.run(async () => {
      const id = cellId(rowId, segmentId);
      const cur = await d.flow.getCell(id);
      if (cur && cur.text === text) return ok(cur);
      const v = validate(FlowCellSchema, { id, roundId, rowId, segmentId, text, marks: cur?.marks ?? [], updatedAt: d.clock.now() }, { text: 'Note' });
      if (v.ok) await d.flow.putCell(v.value);
      return v;
    });

export const toggleMark = (d: D<'flow' | 'clock' | 'tx'>) =>
  (roundId: string, rowId: string, segmentId: string, mark: Mark): Promise<Result<FlowCell>> =>
    d.tx.run(async () => {
      const id = cellId(rowId, segmentId);
      const cur = await d.flow.getCell(id);
      const marks = cur?.marks ?? [];
      const next = marks.includes(mark) ? marks.filter((m) => m !== mark) : [...marks, mark];
      const v = validate(FlowCellSchema, { id, roundId, rowId, segmentId, text: cur?.text ?? '', marks: next, updatedAt: d.clock.now() });
      if (v.ok) await d.flow.putCell(v.value);
      return v;
    });

export const deleteRow = (d: D<'flow'>) => async (rowId: string): Promise<Result<true>> => {
  await d.flow.deleteRow(rowId);
  return ok(true);
};

/** Name an argument (its head row). Empty clears it. An AI title never replaces one the judge wrote. */
export const setArgumentTitle = (d: D<'flow' | 'clock' | 'tx'>) =>
  (roundId: string, rowId: string, title: string, by: 'ai' | 'judge'): Promise<Result<FlowRow>> =>
    d.tx.run(async () => {
      const row = (await d.flow.rows(roundId)).find((r) => r.id === rowId);
      if (!row) return err(notFound('row', rowId));
      if (by === 'ai' && row.titleBy === 'judge' && row.title) return ok(row);
      const t = title.trim().replace(/\s+/g, ' ');
      const updatedAt = d.clock.now();
      const next: FlowRow = t ? { ...row, title: t.slice(0, 60), titleBy: by, updatedAt } : { ...row, title: undefined, titleBy: undefined, updatedAt };
      const v = validate(FlowRowSchema, next, { title: 'Title' });
      if (v.ok) await d.flow.putRow(v.value);
      return v;
    });

/**
 * Starter rows for the layout the judge will see. A round flowed per case keeps its sheets; a new round (no rows
 * yet) or one already on the single sheet gets single-sheet rows when that's the layout.
 */
export const ensureStarterRows = (d: D<'flow' | 'clock' | 'ids' | 'tx'>) => (roundId: string, format: Format, layout: SheetLayout, min = 8): Promise<void> =>
  d.tx.run(async () => {
    const rows = await d.flow.rows(roundId);
    const sheets = sheetsFor(format, layout, rows);
    if (sheets[0]?.id === ALL_SHEET_ID && rows.some((r) => r.sheetId !== ALL_SHEET_ID)) return; // per-case round shown on one sheet
    await ensureRows(d)(roundId, sheets.map((s) => s.id), min);
  });
